// Stripe 서비스 통합 파일
import { loadStripe, Stripe } from '@stripe/stripe-js';

// API 기본 URL 설정
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://us-central1-scanvcf-375fb.cloudfunctions.net';

// Stripe 초기화 (동적 로딩)
let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = async (publishableKey?: string) => {
  if (!stripePromise) {
    const key = publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RU0SC05cgHGTQYU6bwEkJpr9dIwlgVQSyhZ6IN1gukZ0M8mkcdQLIy96cf72JCSnGiXMDqpsntyQMCOgqOsA9Is00fUBe4Jhx';
    stripePromise = loadStripe(key);
  }
  return stripePromise;
};

// 에러 타입 정의
export interface StripeError {
  type: string;
  message: string;
  code?: string;
  decline_code?: string;
}

// API 응답 타입 정의
export interface CheckoutSessionResponse {
  sessionId: string;
}

export interface RefundResponse {
  success: boolean;
  refundId: string;
  amount: number;
  status: string;
}

export interface PaymentStatusResponse {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
}

export interface SubscriptionCancelResponse {
  success: boolean;
  subscriptionId: string;
  status: string;
  canceledAt: number;
}

// 현재 앱 가격 (USD) - cents 단위
export const CURRENT_APP_PRICING = {
  CARD_ONLY: {
    monthly: 300, // US$3.00 (cents)
    yearly: 3000, // US$30.00 (cents)
  },
  STANDARD: {
    monthly: 1000, // US$10.00 (cents)
    yearly: 10000, // US$100.00 (cents)
  },
  PRO: {
    monthly: 2300, // US$23.00 (cents)
    yearly: 23000, // US$230.00 (cents)
  },
};

// 플랜 타입 정의
export const PLAN_TYPES = {
  CARD_ONLY: 'card_only',
  STANDARD: 'standard',
  PRO: 'pro',
} as const;

// 결제 기간 정의
export const BILLING_PERIODS = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
} as const;

// 타입 정의
export type PlanType = typeof PLAN_TYPES[keyof typeof PLAN_TYPES];
export type BillingPeriod = typeof BILLING_PERIODS[keyof typeof BILLING_PERIODS];

// 에러 처리 유틸리티
const handleStripeError = (error: any): StripeError => {
  if (error.type) {
    // Stripe 에러
    switch (error.type) {
      case 'StripeCardError':
        return {
          type: 'card_error',
          message: error.message || '카드 오류가 발생했습니다.',
          code: error.code,
          decline_code: error.decline_code,
        };
      case 'StripeInvalidRequestError':
        return {
          type: 'invalid_request',
          message: error.message || '잘못된 요청입니다.',
          code: error.code,
        };
      case 'StripeAPIError':
        return {
          type: 'api_error',
          message: error.message || 'Stripe API 오류가 발생했습니다.',
          code: error.code,
        };
      case 'StripeConnectionError':
        return {
          type: 'connection_error',
          message: '네트워크 연결 오류가 발생했습니다.',
          code: error.code,
        };
      case 'StripeAuthenticationError':
        return {
          type: 'authentication_error',
          message: '인증 오류가 발생했습니다.',
          code: error.code,
        };
      default:
        return {
          type: 'unknown_error',
          message: error.message || '알 수 없는 오류가 발생했습니다.',
          code: error.code,
        };
    }
  } else if (error.status) {
    // HTTP 에러
    switch (error.status) {
      case 400:
        return {
          type: 'bad_request',
          message: '잘못된 요청입니다.',
          code: 'BAD_REQUEST',
        };
      case 401:
        return {
          type: 'unauthorized',
          message: '인증이 필요합니다.',
          code: 'UNAUTHORIZED',
        };
      case 403:
        return {
          type: 'forbidden',
          message: '접근이 거부되었습니다.',
          code: 'FORBIDDEN',
        };
      case 404:
        return {
          type: 'not_found',
          message: '요청한 리소스를 찾을 수 없습니다.',
          code: 'NOT_FOUND',
        };
      case 500:
        return {
          type: 'server_error',
          message: '서버 오류가 발생했습니다.',
          code: 'SERVER_ERROR',
        };
      default:
        return {
          type: 'http_error',
          message: `HTTP 오류 ${error.status}: ${error.message || '알 수 없는 오류'}`,
          code: `HTTP_${error.status}`,
        };
    }
  } else {
    // 일반 에러
    return {
      type: 'general_error',
      message: error.message || '알 수 없는 오류가 발생했습니다.',
    };
  }
};

// 체크아웃 세션 생성
export const createCheckoutSession = async (
  planType: PlanType,
  billingPeriod: BillingPeriod,
  userId: string
): Promise<CheckoutSessionResponse> => {
  try {
    const amount = CURRENT_APP_PRICING[planType.toUpperCase() as keyof typeof CURRENT_APP_PRICING][billingPeriod];
    
    const response = await fetch(`${API_BASE_URL}/createCheckoutSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
      body: JSON.stringify({
        planType,
        billingPeriod,
        amount,
        userId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw {
        status: response.status,
        message: errorData.error?.message || 'Failed to create checkout session',
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating checkout session:', error);
    const stripeError = handleStripeError(error);
    throw stripeError;
  }
};

// Stripe 체크아웃으로 리다이렉트
export const redirectToCheckout = async (sessionId: string, publishableKey?: string): Promise<void> => {
  try {
    const stripe = await getStripe(publishableKey);
    if (!stripe) {
      throw new Error('Stripe failed to load');
    }
    const { error } = await stripe.redirectToCheckout({ sessionId });
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    const stripeError = handleStripeError(error);
    throw stripeError;
  }
};

// 🆕 환불 처리 (인증 + DB 즉시 반영)
export const processRefund = async (
  paymentIntentId: string,
  amount?: number
): Promise<RefundResponse> => {
  try {
    console.log('🔄 [stripeService] processRefund called with:', { paymentIntentId, amount });
    
    const response = await fetch(`${API_BASE_URL}/processRefundHttp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
      body: JSON.stringify({
        paymentIntentId,
        amount,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw {
        status: response.status,
        message: errorData.error?.message || 'Failed to process refund',
      };
    }

    const result = await response.json();
    console.log('✅ [stripeService] processRefund success:', result);
    return result;
  } catch (error) {
    console.error('❌ [stripeService] Error processing refund:', error);
    const stripeError = handleStripeError(error);
    throw stripeError;
  }
};

// 결제 상태 확인
export const checkPaymentStatus = async (
  paymentIntentId: string
): Promise<PaymentStatusResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/checkPaymentStatus?paymentIntentId=${paymentIntentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${await getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw {
        status: response.status,
        message: errorData.error?.message || 'Failed to check payment status',
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking payment status:', error);
    const stripeError = handleStripeError(error);
    throw stripeError;
  }
};

// 🆕 WooCommerce 방식 구독 취소 (자동 리펀드 포함)
export const cancelSubscription = async (
  subscriptionId: string
): Promise<SubscriptionCancelResponse> => {
  console.log('🔄 [stripeService] WooCommerce 방식 구독 취소 및 자동 리펀드 시작:', subscriptionId);
  
  if (!subscriptionId || typeof subscriptionId !== 'string') {
    console.error('❌ [stripeService] Invalid subscriptionId:', subscriptionId);
    throw {
      type: 'validation_error',
      message: 'Invalid subscription ID provided',
      code: 'INVALID_SUBSCRIPTION_ID'
    };
  }

  // Stripe 구독 ID 형식 검증
  if (!subscriptionId.startsWith('sub_')) {
    console.error('❌ [stripeService] Invalid Stripe subscription ID format:', subscriptionId);
    throw {
      type: 'validation_error',
      message: 'Invalid Stripe subscription ID format',
      code: 'INVALID_STRIPE_ID_FORMAT'
    };
  }

  try {
    const authToken = await getAuthToken();
    
    console.log('📤 [stripeService] Sending cancel request with auto-refund to:', `${API_BASE_URL}/cancelSubscriptionWithRefund`);
    
    const response = await fetch(`${API_BASE_URL}/cancelSubscriptionWithRefund`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ subscriptionId }),
    });

    const responseText = await response.text();
    console.log('📥 [stripeService] Raw response:', responseText);
    console.log('📥 [stripeService] Response status:', response.status);
    console.log('📥 [stripeService] Response ok:', response.ok);

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { error: { message: responseText } };
      }
      console.error('❌ [stripeService] API Error:', errorData);
      throw {
        status: response.status,
        message: errorData.error?.message || 'Failed to cancel subscription with refund',
      };
    }

    const result = JSON.parse(responseText);
    console.log('✅ [stripeService] Cancel subscription with auto-refund success:', result);
    return result;
  } catch (error) {
    console.error('❌ [stripeService] Error canceling subscription with refund:', error);
    throw handleStripeError(error);
  }
};

// 인증 토큰 가져오기 함수 개선
const getAuthToken = async (): Promise<string> => {
  try {
    console.log('🔄 [stripeService] Getting auth token...');
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.error('❌ [stripeService] User not authenticated');
      throw new Error('User not authenticated');
    }
    
    console.log('✅ [stripeService] User authenticated:', user.uid);
    
    // 토큰 새로 고침 강제
    const token = await user.getIdToken(true);
    console.log('✅ [stripeService] Token obtained, length:', token.length);
    return token;
  } catch (error) {
    console.error('❌ [stripeService] Error getting auth token:', error);
    throw new Error('Authentication failed');
  }
};

// 디버깅을 위한 헬퍼 함수들 (브라우저 콘솔에서 사용 가능)
export const debugAuth = async () => {
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    
    console.log('🔍 [Debug] Current user:', user);
    
    if (user) {
      const token = await user.getIdToken();
      console.log('🔍 [Debug] Token length:', token.length);
      console.log('🔍 [Debug] Token preview:', token.substring(0, 50) + '...');
    }
    
    return { user, token: user ? await user.getIdToken() : null };
  } catch (error) {
    console.error('❌ [Debug] Auth error:', error);
    throw error;
  }
};

export const debugApiCall = async (subscriptionId: string) => {
  try {
    const { user, token } = await debugAuth();
    
    if (!token) {
      throw new Error('No auth token available');
    }
    
    console.log('🔍 [Debug] Making API call with subscriptionId:', subscriptionId);
    
    const response = await fetch('https://us-central1-scanvcf-375fb.cloudfunctions.net/cancelSubscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ subscriptionId })
    });
    
    const responseText = await response.text();
    console.log('🔍 [Debug] Raw response:', responseText);
    console.log('🔍 [Debug] Response status:', response.status);
    
    return { response, responseText };
  } catch (error) {
    console.error('❌ [Debug] API call error:', error);
    throw error;
  }
};

// Firestore 상태 직접 확인 도구
export const debugFirestoreStatus = async () => {
  try {
    const { getAuth } = await import('firebase/auth');
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    console.log('🔍 [Debug] Checking Firestore status for user:', user.uid);
    
    const subscriptionRef = doc(db, 'users', user.uid, 'subscription', 'status');
    const subscriptionDoc = await getDoc(subscriptionRef);
    
    if (subscriptionDoc.exists()) {
      const subscriptionData = subscriptionDoc.data();
      console.log('🔍 [Debug] Current Firestore subscription data:', subscriptionData);
      return subscriptionData;
    } else {
      console.log('❌ [Debug] No subscription document found');
      return null;
    }
  } catch (error) {
    console.error('❌ [Debug] Firestore check error:', error);
    throw error;
  }
};

// 강제 상태 초기화 도구
export const debugForceRefresh = () => {
  console.log('🔄 [Debug] Forcing complete refresh...');
  localStorage.clear();
  sessionStorage.clear();
  window.location.reload();
};

// 전역 디버깅 도구 등록 (브라우저 콘솔에서 사용 가능)
if (typeof window !== 'undefined') {
  (window as any).debugApiCall = debugApiCall;
  (window as any).debugAuth = debugAuth;
  (window as any).debugFirestoreStatus = debugFirestoreStatus;
  (window as any).debugForceRefresh = debugForceRefresh;
  
  console.log('🛠️ [Debug] Debug tools available in console:');
  console.log('  - debugApiCall(subscriptionId)');
  console.log('  - debugAuth()');
  console.log('  - debugFirestoreStatus()');
  console.log('  - debugForceRefresh()');
}

// 향후 사용을 위한 Stripe 인스턴스 내보내기
export { stripePromise };
